import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const PreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`

const PreviewContainer = styled.div`
  width: ${p => (p.$isPdf ? 'min(210mm, 90vw)' : '90%')};
  height: 90vh;
  border-radius: 4px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  overflow: hidden;
`

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }
`

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`

const PDFIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
`

const ErrorContainer = styled.div`
  padding: 60px 40px;
  text-align: center;
  color: #666;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
`

const ErrorIcon = styled.p`
  font-size: 48px;
  margin-bottom: 20px;
  opacity: 0.5;
`

const ErrorTitle = styled.p`
  font-size: 18px;
  margin-bottom: 12px;
  font-weight: 600;
  color: #333;
`

const ErrorMessage = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  max-width: 400px;
`

const ErrorHint = styled.p`
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 12px;
  max-width: 400px;
`

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
`

const actionStyles = `
  display: inline-block;
  padding: 12px 28px;
  background: rgba(40, 40, 40, 0.9);
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.2;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(40, 40, 40, 1);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
    transform: none;
  }
`

const DownloadLink = styled.a`
  ${actionStyles}
`

const ReloadButton = styled.button`
  ${actionStyles}
`

/**
 * Fullscreen preview overlay for images and PDFs.
 * Load errors are handled declaratively via state – no imperative DOM
 * manipulation, no dynamic createRoot (see HOR-11).
 *
 * HOR-69: Der Aufrufer prueft die presigned URL VOR dem Oeffnen (siehe presignedUrl.js) und
 * laedt bei Ablauf frische Links nach. Schlaegt das fehl, kommt `refreshFailed` an und wir
 * zeigen den Fallback statt des iframes – ein iframe wuerde bei einer abgelaufenen URL nur
 * rohes S3-XML rendern, ohne onError. Ueber `onReload` (async, liefert true bei Erfolg) kann
 * der Nutzer den Link aus dem Fallback heraus erneuern, ohne die Vorschau zu schliessen.
 */
export default function FilePreviewOverlay({ url, isPdf, onClose, onReload, refreshFailed = false }) {
  const [previewError, setPreviewError] = useState(false)
  const [reloading, setReloading] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
    }
  }, [])

  // Opening a different file must never show a stale error state.
  useEffect(() => {
    setPreviewError(false)
  }, [url, isPdf])

  const canReload = typeof onReload === 'function'

  const handleReload = async () => {
    if (!canReload || reloading) return

    setReloading(true)

    let ok = false

    try {
      ok = (await onReload()) === true
    } catch (e) {
      ok = false
    }

    if (!mountedRef.current) return

    setReloading(false)

    if (ok) setPreviewError(false)
  }

  const showFallback = refreshFailed || previewError || !url
  // Ein Download-Link auf eine abgelaufene URL wuerde ebenfalls nur das S3-Fehlerdokument liefern.
  const showDownload = isPdf && !!url && !refreshFailed

  const actions =
    showDownload || canReload ? (
      <ActionRow>
        {showDownload && (
          <DownloadLink href={url} download target="_blank" rel="noreferrer">
            {i18n('verification.modal.pdfDownload')}
          </DownloadLink>
        )}
        {canReload && (
          <ReloadButton type="button" onClick={handleReload} disabled={reloading}>
            {reloading ? i18n('verification.previewRefresh.reloading') : i18n('verification.previewRefresh.reload')}
          </ReloadButton>
        )}
      </ActionRow>
    ) : null

  const renderContent = () => {
    if (showFallback) {
      return (
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          {isPdf && <ErrorTitle>{i18n('verification.modal.pdfError')}</ErrorTitle>}
          <ErrorMessage>{isPdf ? i18n('verification.modal.pdfErrorMsg') : i18n('verification.modal.imageError')}</ErrorMessage>
          {refreshFailed && <ErrorHint>{i18n('verification.previewRefresh.refreshFailed')}</ErrorHint>}
          {actions}
        </ErrorContainer>
      )
    }

    if (isPdf) {
      return <PDFIframe src={`${url}#view=FitH`} title={i18n('verification.modal.pdfPreview')} onError={() => setPreviewError(true)} />
    }

    return <PreviewImage src={url} alt={i18n('verification.modal.preview')} onClick={e => e.stopPropagation()} onError={() => setPreviewError(true)} />
  }

  return (
    <PreviewOverlay onClick={onClose}>
      <PreviewContainer $isPdf={isPdf} onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>✕</CloseButton>
        {renderContent()}
      </PreviewContainer>
    </PreviewOverlay>
  )
}
